import { DateTime } from 'luxon';
import moment from 'moment-timezone';
class MedelyDateTime {
    dateTimeString;
    dateTime;
    tz;
    /**
     * Creates a new instance of MedelyDateTime
     * @param dateTimeString The string used to instantiate the DateTime object. Typically will come from a
     * network request and will be the exact string as saved in the database
     * @param tz The timezone to associate with the MedelyDateTime
     * @param keepLocalTime If true, will not adjust the hours, minutes, etc, but will simply assign the timezone.
     *   For example, 09:00 UTC will become 09:00 EST (instead of 04:00).
     *
     */
    constructor(dateTimeString, { tz, keepLocalTime }) {
        this.dateTimeString = dateTimeString;
        this.tz = tz;
        if (!tz) {
            throw new Error('Timezone is required to instantiate a MedelyDateTime');
        }
        this.dateTime = (() => {
            let dateTime = DateTime.fromSQL(dateTimeString, {
                setZone: true,
            });
            if (!dateTime.isValid) {
                dateTime = DateTime.fromISO(dateTimeString, { setZone: true });
                if (!dateTime.isValid) {
                    throw new Error('Unable to parse datetime string');
                }
            }
            return tz
                ? dateTime.set({ second: 0, millisecond: 0 }).setZone(tz, { keepLocalTime })
                : dateTime.set({ second: 0, millisecond: 0 });
        })();
    }
    static fromMoment(moment, { tz, keepLocalTime = true }) {
        const dateTimeString = moment.format();
        return new MedelyDateTime(dateTimeString, { tz, keepLocalTime });
    }
    static now({ tz }) {
        const now = new Date();
        return new MedelyDateTime(now.toISOString(), { tz });
    }
    /**
     *
     * This method is meant to be an escape hatch to help us incremetnally adopt this library while moving
     * away from using moment directly. It should be used sparingly, where it isn't yet possible/realistic
     * to move completely to this library yet.
     * @memberof MedelyDateTime
     */
    toMoment() {
        return moment.tz(this.toISO(), this.zoneName());
    }
    /**
     *
     * This method is used to assign a timezone to a MedelyDateTime _without_ changing the local time.
     * this will often be used to display job start/end times, since we want to show the actual local
     * time of the job, regardless of what timezone the user is in
     * @param tz The timezone to assign to the MedelyDateTime
     * @returns The MedelyDateTime object, set in the given timezone
     * @memberof MedelyDateTime
     */
    assignTimeZone(tz) {
        this.dateTime = this.dateTime.setZone(tz, { keepLocalTime: true });
        return this;
    }
    /**
     *
     * This method is used to determine the difference between two dates in a given unit.
     * The returned number is a truncated integer in the given unit (years, months, weeks, days, hours, minutes)
     * @param comparisonDateTime A MedelyDateTime to compare
     * @param unit The unit of time to use to compare the dates - 'years' | 'months' | 'weeks | 'days' | 'hours' | 'minutes'
     * @returns The MedelyDateTime object, set in the given timezone
     * @memberof MedelyDateTime
     */
    difference(comparisonDateTime, unit, precision = 0) {
        const diff = comparisonDateTime.dateTime.diff(this.dateTime, unit)[unit];
        const multiplier = Math.pow(10, precision);
        return Math.floor(diff * multiplier) / multiplier;
    }
    /**
     *
     * This method is used to display a string of the MedelyDateTime in a given format
     * @param formatString The string to use as the format
     * @returns A string representation of the MedelyDateTime, formatted according to the formatString parameter
     * @memberof MedelyDateTime
     */
    format(formatString) {
        return this.dateTime.toFormat(formatString);
    }
    /**
     *
     * This method is used to determine is a MedelyDateTime is between two other MedelyDateTimes
     * @param rangeStart A MedelyDateTime to compare as the start of the range
     * @param rangeEnd A MedelyDateTime to compare as the end of the range
     * @param options options object: inclusiveUnit - if given will compare inclusively at the given duration unit
     * @returns The MedelyDateTime object, set in the given timezone
     * @memberof MedelyDateTime
     */
    isBetween(rangeStart, rangeEnd, { inclusiveUnit } = {}) {
        const startDateTime = rangeStart.dateTime;
        const endDateTime = rangeEnd.dateTime;
        const isBetweenExclusive = startDateTime < this.dateTime && endDateTime > this.dateTime;
        if (!!inclusiveUnit) {
            return (this.dateTime.hasSame(startDateTime, inclusiveUnit) ||
                this.dateTime.hasSame(endDateTime, inclusiveUnit) ||
                isBetweenExclusive);
        }
        return isBetweenExclusive;
    }
    /**
     *
     * This method is used to determine is a MedelyDateTime is before another MedelyDateTime
     * @param comparison A MedelyDateTime to compare
     * @param options options object: inclusiveUnit - if given will compare inclusively at the given duration unit
     * @returns boolean
     * @memberof MedelyDateTime
     */
    isBefore(comparison, { inclusiveUnit } = {}) {
        const comparisonDateTime = comparison.dateTime;
        const isBeforeExclusive = this.dateTime < comparisonDateTime;
        if (!!inclusiveUnit) {
            return this.dateTime.hasSame(comparisonDateTime, inclusiveUnit) || isBeforeExclusive;
        }
        return isBeforeExclusive;
    }
    /**
     *
     * This method is used to determine is a MedelyDateTime is after another MedelyDateTime
     * @param comparison A MedelyDateTime to compare
     * @param options options object: inclusiveUnit - if given will compare inclusively at the given duration unit
     * @returns boolean
     * @memberof MedelyDateTime
     */
    isAfter(comparison, { inclusiveUnit } = {}) {
        const comparisonDateTime = comparison.dateTime;
        const isAfterExclusive = this.dateTime > comparisonDateTime;
        if (!!inclusiveUnit) {
            return this.dateTime.hasSame(comparisonDateTime, inclusiveUnit) || isAfterExclusive;
        }
        return isAfterExclusive;
    }
    isValid() {
        return this.dateTime.isValid;
    }
    /**
     *
     * This method creates a new MedelyDateTime instance with the specified date math applied
     * @param minusArg Object that defines what to subtract from the MedelyDateTime
     * @returns A new MedelyDateTime with the date math applied
     * @memberof MedelyDateTime
     */
    minus(minusArg) {
        const newDateTime = this.dateTime.minus(minusArg);
        return new MedelyDateTime(newDateTime.toISO(), { tz: this.tz });
    }
    /**
     *
     * This method creates a new MedelyDateTime instance with the specified date math applied
     * @param plusArg Object that defines what to add to the MedelyDateTime
     * @returns A new MedelyDateTime with the date math applied
     * @memberof MedelyDateTime
     */
    plus(plusArg) {
        const newDateTime = this.dateTime.plus(plusArg);
        return new MedelyDateTime(newDateTime.toISO(), { tz: this.tz });
    }
    /**
     *
     * Creates a MedelyDateTime at the start of the given unit
     * @returns A new MedelyDateTime
     * @memberof MedelyDateTime
     */
    startOf(unit) {
        // ISO standard considers a week as Monday - Sunday.  We think of it as Sunday - Saturday.
        // Need to customize how this method works when unit = 'week'
        if (unit === 'week') {
            const sundayStartOfWeek = this.dateTime
                .startOf('week')
                .plus({ week: this.dateTime.weekdayShort === 'Sun' ? 1 : 0 })
                .minus({ day: 1 });
            return new MedelyDateTime(sundayStartOfWeek.toISO(), { tz: this.tz });
        }
        const newDateTime = this.dateTime.startOf(unit);
        return new MedelyDateTime(newDateTime.toISO(), { tz: this.tz });
    }
    /**
     *
     * Creates a MedelyDateTime at the start of the given unit
     * @returns A new MedelyDateTime
     * @memberof MedelyDateTime
     */
    endOf(unit) {
        // ISO standard considers a week as Monday - Sunday.  We think of it as Sunday - Saturday.
        // Need to customize how this method works when unit = 'week'
        if (unit === 'week') {
            const saturdayEndOfWeek = this.dateTime
                .endOf('week')
                .plus({ week: this.dateTime.weekdayShort === 'Sun' ? 1 : 0 })
                .minus({ day: 1 });
            return new MedelyDateTime(saturdayEndOfWeek.toISO(), { tz: this.tz });
        }
        const newDateTime = this.dateTime.endOf(unit);
        return new MedelyDateTime(newDateTime.toISO(), { tz: this.tz });
    }
    /**
     *
     * This method should be used when sending a MedelyDateTime in a network request
     * @returns An ISO string of the MedelyDateTime
     * @memberof MedelyDateTime
     */
    toISO() {
        return this.dateTime.toISO();
    }
    /**
     *
     * @returns An ISO date string of the MedelyDateTime
     * @memberof MedelyDateTime
     */
    toISODate() {
        return this.dateTime.toISODate();
    }
    /**
     *
     * @returns An ISO time string of the MedelyDateTime
     * @memberof MedelyDateTime
     */
    toISOTime() {
        return this.dateTime.toISOTime();
    }
    /**
     *
     * This method is used to display a string of the MedelyDateTime in a given format
     * @param formatString The string to use as the format
     * @returns A string representation of the MedelyDateTime, formatted according to the formatString parameter
     * @memberof MedelyDateTime
     */
    toLocaleString(formatObj) {
        return this.dateTime.toLocaleString(formatObj);
    }
    /**
     *
     * @returns The name of the timezone the MedelyDateTime is in
     * @memberof MedelyDateTime
     */
    zoneName() {
        return this.dateTime.zoneName;
    }
    /*
     * DATE AND TIME FORMAT GETTERS
     */
    /**
     *
     * returns a time in the format 23:57
     * @memberof MedelyDateTime
     */
    get militaryTime() {
        return this.dateTime.toLocaleString(DateTime.TIME_24_SIMPLE);
    }
    /**
     *
     * returns a time in the format 11:57 PM
     * @memberof MedelyDateTime
     */
    get twelveHourTime() {
        return this.dateTime.toLocaleString(DateTime.TIME_SIMPLE);
    }
    /**
     *
     * returns a date in the format 1/2/23
     * @memberof MedelyDateTime
     */
    get dateShort() {
        return this.dateTime.toLocaleString({ month: 'numeric', day: 'numeric', year: '2-digit' });
    }
    /**
     *
     * returns a date in the format Wednesday, 3/22/23
     * @memberof MedelyDateTime
     */
    get dayOfWeekHighlight() {
        return this.dateTime.toLocaleString({
            weekday: 'long',
            month: 'numeric',
            day: 'numeric',
            year: '2-digit',
        });
    }
    /**
     *
     * returns a date in the format March 23
     * @memberof MedelyDateTime
     */
    get monthHighlight() {
        return this.dateTime.toLocaleString({ month: 'long', year: '2-digit' });
    }
    /**
     *
     * returns a date in format Wednesday, 3/22/23. replaces day of the week with
     * "Today" or "Tomorrow" if applicable
     * @memberof MedelyDateTime
     */
    get humanizedDate() {
        const now = MedelyDateTime.now({ tz: this.tz });
        const formattedDate = this.dateShort;
        const isToday = now.dateTime.hasSame(this.dateTime, 'day');
        if (isToday) {
            return `Today, ${formattedDate}`;
        }
        const isTomorrow = now.plus({ days: 1 }).toLocaleString() === this.toLocaleString();
        if (isTomorrow) {
            return `Tomorrow, ${formattedDate}`;
        }
        return this.dayOfWeekHighlight;
    }
}
export { MedelyDateTime };
